import { motion } from "framer-motion";
import {
  ArrowLeft,
  
  Plane,
  TrainFront,
  Moon,
  CreditCard,
  Smartphone,
  Ticket,
  Ship,
  
  Clock,
  Bike,
  AlertTriangle,
  Route,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Reveal } from "@/components/motion";
import { GygTourBand } from "@/components/GetYourGuide";
import { Seo } from "@/components/Seo";
import { ShareButton } from "@/components/ShareButton";
import { ShareBar } from "@/components/ShareBar";
import { ExploreLinks } from "@/components/ExploreLinks";

const SITE = "https://cphhighlights.com";

const BREADCRUMB = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Copenhagen Transport", item: `${SITE}/transport` },
  ],
};

const WEBPAGE = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Copenhagen Public Transport: Metro, Tickets & Getting Around",
  url: `${SITE}/transport`,
  description:
    "All you need to know about biking in Copenhagen — the complete Copenhagen transport guide: the Metro from the airport to Copenhagen. All about tickets with the Copenhagen Card, DSB and Rejsekort apps. Copenhagen harbour buses, city bikes and late-night trains.",
  isPartOf: { "@type": "WebSite", name: "CPH Highlights", url: `${SITE}/` },
  publisher: { "@type": "Organization", name: "CPH Highlights", url: `${SITE}/` },
};

const TICKETS = [
  {
    icon: Smartphone,
    tag: "BEST FOR THE INNER CITY",
    name: "City Pass",
    color: "#1E3A8A",
    link: "https://www.rejsebillet.dk/da-dk",
    body:
      "The City Pass lives inside the Rejsebillet app — buy it there and it sits on your phone. It gives unlimited travel in the inner city: Metro, buses, S-trains and harbour buses, including the airport ride.",
    points: [
      "100 kr. for 24 hours in the inner city",
      "160 kr. (48h) · 220 kr. (72h) · 280 kr. (96h)",
      "Unlimited Metro, bus, S-train and harbour bus",
      "Buy for the whole group in one go — only one of you needs the app",
    ],
  },
  {
    icon: Ticket,
    tag: "HOW LOCALS DO IT",
    name: "Rejsekort app",
    color: "#0E7C5A",
    link: "https://www.rejsekortapp.dk/da-dk",
    body:
      "This is how Copenhageners do it — check in on your phone just before you board, check out when you get off, and the fare is calculated automatically. If you plan to walk and maybe hop on a metro or bus, it's worth it.",
    points: [
      "The way locals pay for transport",
      "Check in and out on your phone — no paper tickets",
      "Check in travel companions on the same phone — the whole group can ride on one app",
      "Fare worked out automatically, often cheaper per ride",
    ],
  },
  {
    icon: CreditCard,
    tag: "SIGHTSEEING BUNDLE",
    name: "Copenhagen Card",
    color: "#DF3226",
    link: "https://www.copenhagencard.com",
    body:
      "One card, everything included: unlimited public transport across the whole capital region plus free entry to 80+ attractions and museums.",
    points: [
      "Sold as 24, 48, 72, 96 or 120-hour passes",
      "Covers the airport ride and most day trips",
    ],
    math: {
      summary: "Is it worth it? We did the maths",
      body:
        "A classic sightseeing day: Tivoli, Rosenborg Castle and the Glyptoteket — all in one day. On a weekday, separate tickets plus a 24-hour City Pass come to 580 kr. against the 589 kr. card — 9 kr. cheaper without it. On a weekend (Tivoli costs more), it's 620 kr. against 589 kr. — 31 kr. cheaper with the card. Verdict: only worth it if you visit three attractions and ride public transport on the same day — and fitting the pricier sights into one day is tough. We reserve the right for calculation errors and price changes.",
    },
  },
];


export default function TransportPage() {
  return (
    <main data-testid="transport-page" className="pt-16 md:pt-20">
      <Seo
        title="Copenhagen Public Transport: Metro, Airport & Tickets"
        description="Copenhagen transport made simple: Metro from the airport in 15 minutes, City Pass vs Copenhagen Card, harbour buses, night trains and the bike rules."
        path="/transport"
        image="https://images.unsplash.com/photo-1513622470522-26c3c8a854bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(BREADCRUMB)}</script>
        <script type="application/ld+json">{JSON.stringify(WEBPAGE)}</script>
      </Helmet>
      {/* page header */}
      <section className="relative overflow-hidden bg-[#13141A] text-white border-b border-white/20">
        <div className="absolute inset-0">
          <img src={"https://images.unsplash.com/photo-1513622470522-26c3c8a854bc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#13141A] via-[#13141A]/70 to-[#13141A]/40" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="flex items-center gap-3 mb-8">
            <Link
              to="/"
              data-testid="transport-back-home"
              className="inline-flex items-center gap-2 font-body font-700 text-sm bg-black text-white px-5 py-2.5 rounded-full border border-white/20 hover:bg-white hover:text-black transition-colors"
            >
              <ArrowLeft size={16} /> Back to start
            </Link>
            <ShareButton testid="transport-share" />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <p className="font-body font-800 text-xs uppercase tracking-[0.3em] eyebrow text-white">
              GETTING AROUND &amp; TICKETS
            </p>
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="font-display font-900 tracking-tighter leading-[0.85] text-4xl sm:text-5xl lg:text-6xl"
          >
            Copenhagen Public Transport:<br />
            <span className="font-editorial italic font-600">Metro, Buses, Trains &amp; Tickets</span>
          </motion.h1>
          <p className="font-editorial italic font-600 text-xl md:text-2xl text-white/85 mt-4">
            Public transport, made simple.
          </p>
          <p className="font-body text-base md:text-lg text-white/90 max-w-2xl mt-6">
            Copenhagen’s Metro, trains, buses and harbour buses all run on one ticket system — and
            you’ll rarely wait more than a few minutes. Here’s how to get in from the airport, how
            to buy the cheapest ticket, and how late you can travel.
          </p>
          <p data-testid="transport-updated" className="font-body text-xs uppercase tracking-[0.2em] text-white/40 mt-4">
            Last updated: 29 September 2026
          </p>
          <Link
            to="/biking"
            data-testid="transport-hero-bike-btn"
            className="inline-flex items-center gap-2 font-display font-900 text-base bg-[#DF3226] text-white px-6 py-3.5 rounded-full mt-8 shadow-brut hover:-translate-y-0.5 hover:shadow-xl transition-all"
          >
            <Bike size={18} /> Biking guide
          </Link>
        </div>
      </section>

      {/* airport → city */}
      <section className="bg-[#F9F8F6]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-8 md:py-16">
          <Reveal y={40}>
            <div
              data-testid="transport-airport"
              className="bg-[#DF3226] text-white border border-black/10 shadow-brut-lg p-7 md:p-12 flex flex-col md:flex-row md:items-center gap-8"
            >
              <div className="flex items-center gap-4 shrink-0">
                <Plane size={40} className="shrink-0" />
                <span className="font-display font-900 text-4xl">→</span>
                <TrainFront size={40} className="shrink-0" />
              </div>
              <div>
                <h2 className="font-display font-900 tracking-tighter leading-[0.95] text-3xl md:text-4xl">
                  Straight from the airport
                </h2>
                <p className="font-body text-base md:text-lg mt-3 max-w-2xl leading-relaxed">
                  The <strong>Metro (M2)</strong> runs directly from Copenhagen Airport into the city
                  centre in about <strong>15 minutes</strong> — no changes needed.
                </p>
                <p className="font-body text-base md:text-lg mt-4 max-w-2xl leading-relaxed">
                  Heading for the central station or Vesterbro? Take the <strong>train</strong> from
                  the airport instead — it drops you right there.
                </p>
                <p className="font-body text-base md:text-lg mt-4 max-w-2xl leading-relaxed">
                  On a{" "}
                  <Link to="/layover" className="font-800 underline underline-offset-2 hover:opacity-75">
                    long layover
                  </Link>
                  ? You can see the highlights in a few hours.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* buying tickets */}
      <section className="bg-white border-y border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <Reveal>
            <div className="flex items-center gap-3 mb-3">
              <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">
                HOW TO BUY TICKETS
              </p>
            </div>
            <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl max-w-3xl">
              Ways to pay,<br />
              <span className="font-editorial italic font-600">pick what fits your trip</span>
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
            {TICKETS.map((t, i) => (
              <Reveal key={t.name} delay={i * 0.06} y={40}>
                <div
                  data-testid={`transport-ticket-${i}`}
                  className="group bg-[#F9F8F6] border border-zinc-200 shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all p-6 md:p-7 h-full flex flex-col"
                >
                  <div
                    className="w-12 h-12 flex items-center justify-center text-white shrink-0"
                    style={{ backgroundColor: t.color }}
                  >
                    <t.icon size={22} />
                  </div>
                  <span className="font-body font-800 text-[11px] tracking-widest mt-5" style={{ color: t.color }}>
                    {t.tag}
                  </span>
                  <h3 className="font-display font-900 tracking-tight text-2xl md:text-3xl mt-1 leading-none">
                    {t.name}
                  </h3>
                  <p className="font-body text-sm md:text-base text-black/70 mt-4 leading-relaxed">{t.body}</p>
                  <ul className="mt-5 space-y-2 border-t border-dotted border-zinc-300 pt-4">
                    {t.points.map((p) => (
                      <li key={p} className="flex items-start gap-2 font-body text-sm text-black/80">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: t.color }} />
                        {p}
                      </li>
                    ))}
                  </ul>
                  {t.math && (
                    <details
                      data-testid={`transport-ticket-math-${i}`}
                      className="group/details mt-4 border border-zinc-200 bg-white open:shadow-brut"
                    >
                      <summary className="cursor-pointer list-none flex items-center justify-between gap-2 px-4 py-3 font-body font-800 text-sm text-black/80 hover:text-[#DF3226] transition-colors">
                        {t.math.summary}
                        <span className="text-[#DF3226] text-lg leading-none transition-transform group-open/details:rotate-45">+</span>
                      </summary>
                      <p className="px-4 pb-4 font-body text-sm text-black/70 leading-relaxed border-t border-zinc-100 pt-3">
                        {t.math.body}
                      </p>
                    </details>
                  )}
                  {t.link && (
                    <a
                      href={t.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`transport-ticket-link-${i}`}
                      className="mt-5 inline-flex items-center gap-1.5 font-display font-800 text-sm hover:gap-3 transition-all"
                      style={{ color: t.color }}
                    >
                      Get the {t.name}
                    </a>
                  )}
                </div>
              </Reveal>
            ))}
          </div>

          {/* journey planner — official Danish trip planner */}
          <Reveal y={30}>
            <a
              href="https://www.rejseplanen.dk/webapp/index.html?language=en_EN"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="transport-rejseplanen-link"
              className="group mt-8 flex items-start gap-4 bg-[#FCEFC5] text-black border border-black shadow-brut p-6 md:p-7 hover:-translate-y-0.5 transition-transform"
            >
              <Route size={26} className="text-[#DF3226] shrink-0 mt-0.5" />
              <p className="font-body text-sm md:text-base leading-relaxed text-black/75">
                <strong className="text-black">Plan the journey with Rejseplanen.</strong> The
                official Danish journey planner — type any two addresses and it works out the Metro,
                train, bus and walking legs for you, in English.{" "}
                <span className="inline-flex items-center gap-1.5 font-800 text-[#DF3226] group-hover:gap-3 transition-all">
                  Open Rejseplanen
                </span>
              </p>
            </a>
          </Reveal>

          {/* harbour buses note — included in the standard ticket */}
          <Reveal y={30}>
            <div
              data-testid="transport-fact-harbour-buses"
              className="mt-8 flex items-start gap-4 bg-[#FCEFC5] text-black border border-black shadow-brut p-6 md:p-7"
            >
              <Ship size={26} className="text-[#DF3226] shrink-0 mt-0.5" />
              <p className="font-body text-sm md:text-base leading-relaxed">
                <span className="font-display font-900">Good to know — harbour buses count too. </span>
                The yellow harbour buses (lines 991–992) run on the normal ticket system. BUT the
                harbour buses are popular during peak summer, so don't be disappointed if it's full —
                have an alternative ready.
              </p>
            </div>
          </Reveal>
          {/* no ticket barriers — but inspectors fine you */}
          <Reveal y={30}>
            <div
              data-testid="transport-fact-no-barriers"
              className="mt-8 flex items-start gap-4 bg-[#FCEFC5] text-black border border-black shadow-brut p-6 md:p-7"
            >
              <AlertTriangle size={26} className="text-[#DF3226] shrink-0 mt-0.5" />
              <p className="font-body text-sm md:text-base leading-relaxed">
                <span className="font-display font-900">No gates, no barriers — walk straight in. </span>
                There are no ticket barriers on the Metro or trains, so you can walk straight onto
                the platform. But don&apos;t chance it: ticket inspectors ride regularly, check tickets
                mid-journey and hand out a <strong>DKK 750 fine</strong> to anyone without a valid
                ticket. Always buy before you board.
              </p>
            </div>
          </Reveal>
          <Reveal y={30}>
            <p className="font-body text-sm md:text-base text-black/70 mt-5">
              Taking the train further — Helsingør, Roskilde or Malmö?{" "}
              <Link
                to="/trips"
                data-testid="transport-trips-link"
                className="font-800 text-[#DF3226] underline underline-offset-4 hover:text-black transition-colors"
              >
                See our day trips from Copenhagen
              </Link>
              .
            </p>
          </Reveal>
        </div>
      </section>
      <section className="bg-[#13141A] text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <Reveal>
              <div className="flex items-center gap-3 mb-4">
                <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">AFTER DARK</p>
              </div>
              <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
                Travelling late?<br />
                <span className="font-editorial italic font-600">You’re covered</span>
              </h2>
              <p className="font-body text-base md:text-lg text-white/80 mt-6 max-w-xl leading-relaxed">
                The <strong>Metro runs around the clock</strong>, every day of the week — handy for
                late arrivals and early flights. Trains keep the city moving into the small hours too.
              </p>
            </Reveal>

            <Reveal delay={0.08} y={40}>
              <div className="space-y-3">
                <div
                  data-testid="transport-night-weekend"
                  className="bg-white/5 border border-white/15 p-4 flex items-start gap-3"
                >
                  <TrainFront size={20} className="text-[#DF3226] shrink-0 mt-0.5" />
                  <div>
                    <p className="font-display font-800 text-base">Friday &amp; Saturday nights</p>
                    <p className="font-body text-sm text-white/70 mt-1 leading-relaxed">
                      Trains run <strong>all night long</strong> — dance until late and still catch a
                      ride home.
                    </p>
                  </div>
                </div>
                <div
                  data-testid="transport-night-weekday"
                  className="bg-white/5 border border-white/15 p-4 flex items-start gap-3"
                >
                  <Clock size={20} className="text-[#DF3226] shrink-0 mt-0.5" />
                  <div>
                    <p className="font-display font-800 text-base">Weeknights</p>
                    <p className="font-body text-sm text-white/70 mt-1 leading-relaxed">
                      The last train is usually around <strong>01:00</strong> — plan the journey home
                      before your evening runs on.
                    </p>
                  </div>
                </div>
                <div
                  data-testid="transport-night-metro"
                  className="bg-white/5 border border-white/15 p-4 flex items-start gap-3"
                >
                  <TrainFront size={20} className="text-[#DF3226] shrink-0 mt-0.5" />
                  <div>
                    <p className="font-display font-800 text-base">The Metro</p>
                    <p className="font-body text-sm text-white/70 mt-1 leading-relaxed">
                      Runs <strong>24/7</strong>, every day of the week — you can always get home.
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* biking teaser — the full guide moved to /biking (id kept so old #biking links land here) */}
      <section id="biking" data-testid="transport-biking-teaser" className="bg-[#13141A] text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 pb-12 md:pb-16 pt-2 md:pt-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="font-body font-800 text-xs tracking-[0.3em] eyebrow text-[#DF3226]">
              ON TWO WHEELS
            </p>
            <h2 className="font-display font-900 tracking-tighter leading-[0.95] text-3xl md:text-4xl mt-3">
              Biking in Copenhagen{" "}
              <span className="font-editorial italic font-600">has its own guide now</span>
            </h2>
            <p className="font-body text-sm md:text-base text-white/80 mt-3 max-w-xl leading-relaxed">
              The bike-lane rules, the city-bike apps and the guided tours worth booking — all on one page.
            </p>
          </div>
          <Link
            to="/biking"
            data-testid="transport-biking-teaser-link"
            className="shrink-0 inline-flex items-center gap-2 font-display font-900 text-base bg-[#DF3226] text-white px-6 py-3.5 rounded-full shadow-brut hover:-translate-y-0.5 hover:shadow-xl transition-all"
          >
            <Bike size={18} /> Biking guide
          </Link>
        </div>
      </section>

      {/* tourist rules */}
      <section data-testid="transport-tourist-rules" className="bg-[#F9F8F6] text-black border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10 md:py-14">
          <p className="font-body font-800 uppercase tracking-[0.2em] text-xs text-[#DF3226]">
            Tourist tip
          </p>
          <div className="mt-5 max-w-2xl">
            <div className="flex items-start gap-4" data-testid="transport-tourist-rule-2">
              <div>
                <p className="font-display font-900 tracking-tight text-xl md:text-2xl leading-tight">
                  Don&apos;t take regular taxis unless necessary.
                </p>
                <p className="font-body text-sm md:text-base text-black/70 mt-2 leading-relaxed">
                  Taxis are exceptionally expensive — the Metro runs 24/7 and gets you there for a
                  fraction of the price.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#13141A] text-white border-t border-dotted border-zinc-300">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-8">
          <h2 className="font-display font-900 tracking-tighter leading-[0.9] text-4xl md:text-5xl">
            Know how to move?<br />
            <span className="font-editorial italic font-600 text-white">Now build your days</span>
          </h2>
          <Link
            to="/planner"
            data-testid="transport-to-planner"
            className="inline-flex items-center gap-2 font-display font-900 text-lg bg-[#DF3226] text-white px-8 py-4 rounded-full shadow-brut hover:-translate-y-1 hover:shadow-brut-lg transition-all duration-300"
          >
            Open the planner
          </Link>
        </div>
      </section>

      {/* Book a tour — Hop-On Hop-Off sightseeing bus */}
      <GygTourBand tourId="689802" label="BOOK A TOUR" />

      <ExploreLinks
        testid="explore-transport"
        links={[
          { label: "Just passing through? The layover guide", to: "/layover" },
          { label: "Trains out of town — day trips from Copenhagen", to: "/trips" },
          { label: "Choose your base — the area guides", to: "/areas" },
        ]}
      />
      <ShareBar />
    </main>
  );
}
